export interface ParseResult {
  usernames: string[];
  error?: string;
}

// Inputs are guaranteed to be lowercased + deduplicated before leaving this function.
// compareFollows relies on this guarantee — do not skip normalization here.
function extractFromStringListData(items: any[]): string[] {
  const usernames: string[] = [];
  for (const item of items) {
    const data = item?.string_list_data;
    if (Array.isArray(data)) {
      for (const entry of data) {
        const val = entry?.value?.trim();
        if (val) usernames.push(val.toLowerCase());
      }
    }
  }
  return Array.from(new Set(usernames));
}

function parseJSON(raw: string): ParseResult {
  let data: any;
  try {
    data = JSON.parse(raw);
  } catch {
    return { usernames: [], error: 'Invalid JSON file. Please use the file directly from your Instagram data download.' };
  }

  // followers: plain array of objects with string_list_data
  if (Array.isArray(data)) {
    const usernames = extractFromStringListData(data);
    if (usernames.length > 0) return { usernames };
    return { usernames: [], error: 'Could not find any usernames in this file. Make sure you\'re uploading the correct Instagram export file.' };
  }

  // following: { relationships_following: [...] }
  if (data?.relationships_following && Array.isArray(data.relationships_following)) {
    const usernames = extractFromStringListData(data.relationships_following);
    if (usernames.length > 0) return { usernames };
  }

  // Fallback: check any top-level array key
  for (const key of Object.keys(data)) {
    if (Array.isArray(data[key])) {
      const usernames = extractFromStringListData(data[key]);
      if (usernames.length > 0) return { usernames };
    }
  }

  return { usernames: [], error: 'Could not find any usernames in this file. Make sure you\'re uploading the correct Instagram export file.' };
}

function parseHTML(raw: string): ParseResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, 'text/html');
  const links = Array.from(doc.querySelectorAll('a[href*="instagram.com"]'));

  const usernames = links
    .map((link) => {
      const href = (link as HTMLAnchorElement).href || link.getAttribute('href') || '';
      // Extract username from https://www.instagram.com/username/ OR https://www.instagram.com/_u/username/
      const match = href.match(/instagram\.com\/(?:_u\/)?([^/?#]+)/);
      return match ? match[1].toLowerCase() : '';
    })
    .filter(Boolean);

  if (usernames.length > 0) {
    return { usernames: Array.from(new Set(usernames)) };
  }

  // Fallback: grab text content of links that look like usernames
  const allLinks = Array.from(doc.querySelectorAll('a'));
  const fallback = allLinks
    .map((a) => (a.textContent?.trim() || '').toLowerCase())
    .filter((t) => t && !t.startsWith('http') && t.length > 0 && !t.includes(' '));

  if (fallback.length > 0) {
    return { usernames: Array.from(new Set(fallback)) };
  }

  return { usernames: [], error: 'Could not extract usernames from this HTML file. Make sure it\'s from your Instagram data export.' };
}

export function parseInstagramFile(file: File): Promise<ParseResult> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const raw = e.target?.result as string;
      if (!raw) {
        resolve({ usernames: [], error: 'File appears to be empty.' });
        return;
      }

      const name = file.name.toLowerCase();
      if (name.endsWith('.json')) {
        resolve(parseJSON(raw));
      } else if (name.endsWith('.html') || name.endsWith('.htm')) {
        resolve(parseHTML(raw));
      } else {
        // Try JSON first, then HTML
        const jsonResult = parseJSON(raw);
        if (jsonResult.usernames.length > 0) {
          resolve(jsonResult);
        } else {
          resolve(parseHTML(raw));
        }
      }
    };

    reader.onerror = () => resolve({ usernames: [], error: 'Failed to read file.' });
    reader.readAsText(file);
  });
}

export interface ComparisonResults {
  notFollowingBack: string[];   // you follow them, they don't follow you
  youDontFollowBack: string[];  // they follow you, you don't follow them
  mutuals: string[];
}

// Inputs are expected to already be lowercased + deduplicated by the parse layer.
export function compareFollows(following: string[], followers: string[]): ComparisonResults {
  const followingSet = new Set(following);
  const followersSet = new Set(followers);

  const notFollowingBack = following.filter((u) => !followersSet.has(u));
  const youDontFollowBack = followers.filter((u) => !followingSet.has(u));
  const mutuals = following.filter((u) => followersSet.has(u));

  return { notFollowingBack, youDontFollowBack, mutuals };
}
